import React, { type AnchorHTMLAttributes, type ForwardRefExoticComponent } from "react";
import { Accordion } from "@purpur/accordion";
import type { BaseProps } from "@purpur/common-types";
import { Link } from "@purpur/link";
import c from "classnames/bind";

import { rootClassName } from "./constants";
import styles from "./footer.module.scss";

const cx = c.bind(styles);

export type FooterNavigationItemLink = Omit<BaseProps<"a">, "href"> & {
  text: string;
  href: string;
};

export type FooterNavigationItem = {
  heading: string;
  links: FooterNavigationItemLink[];
};

export type CustomLinkType =
  | ForwardRefExoticComponent<AnchorHTMLAttributes<HTMLAnchorElement>>
  | "a";

type NavigationLinksProps = {
  links: FooterNavigationItemLink[];
  customLink?: CustomLinkType;
};

const NavigationLinks = ({ links, customLink: CustomLink }: NavigationLinksProps) => (
  <ul>
    {links.map(({ href, text, ...linkProps }, j) => (
      <li key={j}>
        {CustomLink ? (
          <CustomLink
            {...linkProps}
            href={href}
            className={cx(`${rootClassName}__custom-link-navigation`)}
          >
            {text}
          </CustomLink>
        ) : (
          <Link {...linkProps} variant="navigation" negative href={href} disableVisited={true}>
            {text}
          </Link>
        )}
      </li>
    ))}
  </ul>
);

type FooterNavigationProps = {
  customLink?: CustomLinkType;
  sectionProps?: Omit<BaseProps<"section">, "children">;
  navigationLinks: FooterNavigationItem[];
};

export const FooterNavigation = ({
  navigationLinks,
  sectionProps,
  customLink,
}: FooterNavigationProps) => (
  <>
    <section
      {...sectionProps}
      className={cx(
        `${rootClassName}__navigation`,
        `${rootClassName}__navigation--lg`,
        sectionProps?.className
      )}
    >
      {navigationLinks.map((section, i) => (
        <div key={i} className={cx(`${rootClassName}__navigation-section`)}>
          <h3 className={cx(`${rootClassName}__navigation-section-heading`)}>{section.heading}</h3>
          <NavigationLinks links={section.links} customLink={customLink} />
        </div>
      ))}
    </section>
    <section
      {...sectionProps}
      className={cx(
        `${rootClassName}__navigation`,
        `${rootClassName}__navigation--sm`,
        sectionProps?.className
      )}
    >
      <Accordion negative>
        {navigationLinks.map((section, i) => (
          <Accordion.Item
            key={i}
            title={section.heading}
            headingTag="h2"
            className={cx(`${rootClassName}__navigation-section`)}
          >
            <NavigationLinks links={section.links} customLink={customLink} />
          </Accordion.Item>
        ))}
      </Accordion>
    </section>
  </>
);
